import { QrCode } from "lucide-react";
import { API } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { AthletePhoto, Logo, LicenseStatusBadge } from "@/components/common";

const Row = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-blue-200/70">{label}</div>
    <div className={`mt-0.5 text-sm font-semibold text-white ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
  </div>
);

export function LicenseCard({ lic, settings }) {
  const issued = !!lic.license_number && lic.status !== "pending";
  return (
    <div className="print-area relative overflow-hidden rounded-2xl border border-blue-900/40 bg-[#0F2042] text-white shadow-2xl" data-testid="license-card">
      <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full border-[28px] border-white/[0.04]" />
      <div className="flex items-center gap-3 border-b border-amber-500/70 px-5 py-4 sm:px-6">
        <Logo settings={settings} dark className="h-11 w-11" />
        <div className="min-w-0">
          <div className="font-display text-base font-bold tracking-tight">MSP ITALIA</div>
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-amber-400 sm:text-[11px]">Abilitazione Sportiva Nazionale – Settori Motori</div>
        </div>
        <div className="ml-auto hidden sm:block"><LicenseStatusBadge status={lic.status} testid="license-card-status" /></div>
      </div>
      <div className="grid gap-5 p-5 sm:grid-cols-[auto_1fr_auto] sm:p-6">
        <AthletePhoto athleteId={lic.athlete_id} hasPhoto={lic.athlete_has_photo} name={lic.athlete_name} className="h-32 w-24 sm:h-36 sm:w-28" rounded="rounded-lg" />
        <div className="space-y-3">
          <div className="font-display text-xl font-bold uppercase leading-tight" data-testid="license-card-name">{lic.athlete_name}</div>
          <div className="grid grid-cols-2 gap-3">
            <Row label="N. licenza" value={lic.license_number} mono />
            <Row label="Tessera MSP" value={lic.membership_number} mono />
            <Row label="Disciplina" value={lic.discipline_name} />
            <Row label="Settore" value={lic.sector_name} />
            <Row label="Emissione" value={fmtDate(lic.issue_date)} />
            <Row label="Scadenza" value={fmtDate(lic.expiry_date)} />
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-blue-200/70">Categorie abilitate</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {lic.categories.map((c) => (
                <span key={c.category_id} className="rounded-md bg-white/10 px-2 py-0.5 text-xs">{c.name}{c.valid_until ? ` · fino al ${fmtDate(c.valid_until)}` : ""}</span>
              ))}
            </div>
          </div>
          <div className="sm:hidden"><LicenseStatusBadge status={lic.status} /></div>
        </div>
        <div className="flex flex-col items-center gap-2">
          {issued ? (
            <img src={`${API}/licenses/${lic.id}/qr.png`} alt="QR code licenza" className="h-40 w-40 rounded-xl bg-white p-2 sm:h-44 sm:w-44" data-testid="license-card-qr" />
          ) : (
            <div className="flex h-40 w-40 flex-col items-center justify-center rounded-xl border border-dashed border-white/30 text-center text-xs text-blue-100/70">
              <QrCode className="mb-2 h-8 w-8" />QR disponibile dopo l'approvazione
            </div>
          )}
          <div className="text-[10px] font-semibold uppercase tracking-widest text-blue-200/70">Scansiona per verificare</div>
        </div>
      </div>
      <div className="bg-white/[0.06] px-5 py-2.5 text-center text-xs font-semibold text-blue-50 sm:px-6">Verifica validità tramite Registro Nazionale MSP Italia</div>
    </div>
  );
}
